import React, { useEffect, useState } from 'react'
import { handleImageUpload } from '@/tiptap/lib/tiptap-utils'
import type {
  AnyShapeData,
  InteractionPointData,
} from '@deepguide-ai/react-image-editor'
import { Dialog, DialogContent, DialogTitle } from '@deepguide-ai/ui/dialog'
import { useAtom } from 'jotai'
import { useSetAtom } from 'jotai/react'
import { toast } from 'sonner'
import {
  isImageEditorOpenAtom,
  getImageEditorDataAtom,
  updateImageEditorDataAtom,
  editedScreenshotMapAtom,
} from '@/stores/imageEditorStore'
import { trackEvent } from '@/lib/posthog'
import { trpcClient } from '@/lib/trpc'

const ImageEditor = React.lazy(() =>
  import('@deepguide-ai/react-image-editor').then((module) => ({
    default: module.ImageEditor,
  }))
)

export function ImageEditorDialogHost({
  analyticsSource = 'image_editor',
}: {
  analyticsSource?: string
}) {
  const [isImageEditorOpen, setIsImageEditorOpen] = useAtom(
    isImageEditorOpenAtom
  )
  const [imageEditorData] = useAtom(getImageEditorDataAtom)
  const updateImageEditorData = useSetAtom(updateImageEditorDataAtom)
  const setEditedMap = useSetAtom(editedScreenshotMapAtom)
  const [editorImageSrc, setEditorImageSrc] = useState<string | null>(null)
  const [imageSourceError, setImageSourceError] = useState<string | null>(null)

  useEffect(() => {
    const loadEdited = async () => {
      if (!isImageEditorOpen) return
      const stepId = imageEditorData?.stepId
      if (!stepId) return

      try {
        const { screenshot } =
          await trpcClient.steps.getEditedScreenshotByStepId.query({ stepId })
        if (!screenshot) return

        const editedAnnotation = (screenshot as any)?.editedAnnotation as
          | {
              annotations?: AnyShapeData[]
              interactionPoints?: InteractionPointData[]
            }
          | undefined

        updateImageEditorData({
          ...(screenshot.editedUrl ? { src: screenshot.editedUrl } : {}),
          ...(editedAnnotation?.annotations
            ? { initialAnnotations: editedAnnotation.annotations }
            : {}),
          ...(editedAnnotation?.interactionPoints
            ? { initialInteractionPoints: editedAnnotation.interactionPoints }
            : {}),
        })
      } catch (error) {
        console.error('Failed to load edited image', error)
      }
    }

    void loadEdited()
  }, [isImageEditorOpen, imageEditorData?.stepId, updateImageEditorData])

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    const source = imageEditorData?.src

    setImageSourceError(null)
    if (!isImageEditorOpen || !source) {
      setEditorImageSrc(null)
      return
    }

    // Storage objects require the authenticated app session. The image editor
    // loads with `crossOrigin="anonymous"`, which intentionally omits cookies
    // for the API origin, so download the bytes first and give the canvas a
    // same-page Blob URL instead. Non-storage images keep their existing path.
    if (!source.includes('/api/storage/object/')) {
      setEditorImageSrc(source)
      return
    }

    setEditorImageSrc(null)
    const loadAuthenticatedImage = async () => {
      try {
        const inlineUrl = new URL(source, window.location.origin)
        inlineUrl.searchParams.set('inline', '1')
        const response = await fetch(inlineUrl.toString(), {
          credentials: 'include',
        })
        if (!response.ok) {
          throw new Error(`Failed to load image (${response.status})`)
        }

        const blob = await response.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setEditorImageSrc(objectUrl)
      } catch (error) {
        if (!cancelled) {
          setImageSourceError(
            error instanceof Error ? error.message : 'Failed to load image'
          )
        }
      }
    }

    void loadAuthenticatedImage()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [imageEditorData?.src, isImageEditorOpen])

  return (
    <Dialog
      open={isImageEditorOpen}
      onOpenChange={(open) => {
        if (!open) setIsImageEditorOpen(false)
      }}
    >
      <DialogContent
        closeClassName='hidden'
        // Below package Select/Popover (z-[10002]); they portal to body.
        overlayClassName='z-[10000]'
        className='bg-muted !top-0 !left-0 z-[10000] h-screen w-screen !max-w-none !translate-x-0 !translate-y-0 gap-0 overflow-hidden rounded-none border-0 p-0 shadow-none'
        onPointerDownOutside={(event) => {
          const target = event.target as HTMLElement | null
          if (
            target?.closest('[data-radix-popper-content-wrapper]') ||
            target?.closest('[data-slot="select-content"]')
          ) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement | null
          if (
            target?.closest('[data-radix-popper-content-wrapper]') ||
            target?.closest('[data-slot="select-content"]')
          ) {
            event.preventDefault()
          }
        }}
      >
        <DialogTitle className='sr-only'>Edit image</DialogTitle>
        <React.Suspense
          fallback={
            <div className='bg-background text-muted-foreground flex h-full items-center justify-center text-sm'>
              Loading image editor…
            </div>
          }
        >
          {imageSourceError ? (
            <div className='bg-background text-destructive flex h-full items-center justify-center text-sm'>
              {imageSourceError}
            </div>
          ) : !editorImageSrc ? (
            <div className='bg-background text-muted-foreground flex h-full items-center justify-center text-sm'>
              Loading image…
            </div>
          ) : (
            <ImageEditor
              className='screenata-image-editor'
              src={editorImageSrc}
              stepId={imageEditorData?.stepId || ''}
              initialInteractionPoints={
                imageEditorData?.initialInteractionPoints || []
              }
              initialAnnotations={imageEditorData?.initialAnnotations || []}
              onCancel={() => setIsImageEditorOpen(false)}
              onAnalyticsEvent={(event, properties) => {
                trackEvent(event, {
                  source: analyticsSource,
                  stepId: imageEditorData?.stepId,
                  ...properties,
                })
              }}
              onSave={async (
                imageData: Blob,
                annotations: AnyShapeData[],
                interactionPoints: InteractionPointData[]
              ) => {
                try {
                  const file = new File(
                    [imageData],
                    `edited-screenshot-${Date.now()}.png`,
                    { type: 'image/png' }
                  )
                  const url = await handleImageUpload(file)

                  if (!imageEditorData?.stepId) {
                    toast.error('Unable to save image without a step')
                    return
                  }

                  const stepId = imageEditorData.stepId
                  const { screenshot } = await trpcClient.steps.saveEditedScreenshot.mutate({
                    stepId,
                    editedUrl: url,
                    editedAnnotation: { annotations, interactionPoints },
                  })

                  try {
                    const { step } = await trpcClient.steps.getStepById.query({
                      id: stepId,
                    })
                    const baseWidth =
                      step?.viewportInfo?.width ??
                      step?.viewportCoordinates?.width
                    const baseHeight =
                      step?.viewportInfo?.height ??
                      step?.viewportCoordinates?.height
                    const latest =
                      interactionPoints[interactionPoints.length - 1]

                    if (baseWidth && baseHeight && latest) {
                      const currentCoordinates = step?.viewportCoordinates
                      const viewportCoordinates = currentCoordinates
                        ? {
                            ...currentCoordinates,
                            center: {
                              x: (latest.x / 100) * baseWidth,
                              y: (latest.y / 100) * baseHeight,
                            },
                          }
                        : {
                            topLeft: { x: 0, y: 0 },
                            topRight: { x: baseWidth, y: 0 },
                            bottomLeft: { x: 0, y: baseHeight },
                            bottomRight: { x: baseWidth, y: baseHeight },
                            center: {
                              x: (latest.x / 100) * baseWidth,
                              y: (latest.y / 100) * baseHeight,
                            },
                            width: baseWidth,
                            height: baseHeight,
                          }

                      await trpcClient.steps.updateStep.mutate({
                        id: stepId,
                        viewportCoordinates,
                      })
                      setEditedMap((previous) => ({
                        ...previous,
                        [stepId]: { url, sourceUrl: screenshot.url, viewportCoordinates },
                      }))
                    } else {
                      setEditedMap((previous) => ({
                        ...previous,
                        [stepId]: { url, sourceUrl: screenshot.url },
                      }))
                    }
                  } catch (error) {
                    console.error(
                      'Failed to update screenshot coordinates',
                      error
                    )
                    setEditedMap((previous) => ({
                      ...previous,
                      [stepId]: { url, sourceUrl: screenshot.url },
                    }))
                  }

                  toast.success('Image saved')
                  setIsImageEditorOpen(false)
                } catch (error) {
                  console.error('Failed to save edited screenshot', error)
                  toast.error('Image failed to save, please try later.')
                }
              }}
            />
          )}
        </React.Suspense>
      </DialogContent>
    </Dialog>
  )
}
